import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// components and helpers
import utils from '../../../utils/utils';
import { workspaceActions } from '../WorkspaceActions';
import * as caseActions from '../Case/CaseActions';
import { statusTitle } from './ImportWizard/helpers/StatusTitle';
import Table from '../../Shared/Table';

// mui
import Grid from '@mui/material/Grid';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import IconButton from '@mui/material/IconButton';
import ImportProgressPreview from './ImportWizard/ImportProgressPreview';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { TransitionProps } from '@mui/material/transitions';
import Slide from '@mui/material/Slide';
import { Tooltip } from '@mui/material';
import Trustness from '../../Actions/Trustness';
import Paper from '@mui/material/Paper';
import ErrorsTable from './Errors/ErrorsTable';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const FileList: React.FC = () => {
    const dispatch = useDispatch();
    const [fileName, setFileName] = useState<string>('');
    const location = useLocation();
    const [pageLimit, setPageLimit] = useState<number>(10);
    const [offset, setOffset] = useState<number>(0);
    const [sortField, setSortField] = useState<string>('lastUpdated');
    const [sortDirection, setSortDirection] = useState<string>('DESC');
    const [pageNumber, setPageNumber] = useState<number>(0);
    const [openProgress, setOpenProgress] = useState(false);
    const [openErrorDialog, setOpenErrorDialog] = useState(false);

    const fileImportList = useSelector(
        (state: any) => state.workspace.fileImportList
    );
    const fileImportListLoading = useSelector(
        (state: any) => state.workspace.fileListLoading
    );
    const trustData = useSelector((state: any) => state.cases);
    const currentWorkspace = useSelector(
        (state: any) => state.workspace.currentWorkspace
    );
    const fileImportEventLoading = useSelector(
        (state: any) => state.workspace.fileImportEventLoading
    );

    useEffect(() => {
        handleFetch('initial');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (
            fileImportList &&
            fileImportList.items &&
            fileImportList.items.length > 0
        ) {
            let filterResolved = fileImportList.items.filter(
                ({ status }) => status === 'ERROR' || status === 'SUCCESS'
            );
            let resolvedFileIds = filterResolved.map(({ id }) => id);
            // let fileIds = fileImportList.items.map(({ id }) => id);
            dispatch(caseActions?.checkFileTrustness(resolvedFileIds));
        }

        if (
            !fileImportEventLoading &&
            fileImportList &&
            fileImportList.items &&
            fileImportList.items.some(
                (e) =>
                    e.status === 'RECEIVED' ||
                    (e.status === 'DRAFT' && e.fileName)
            )
        ) {
            let filterPending = fileImportList.items.filter(
                ({ status }) => status === 'RECEIVED' || status === 'DRAFT'
            );
            let pendingFileIds = filterPending.map(({ id }) => id);
            dispatch(workspaceActions.getFileImportProgress(pendingFileIds));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileImportList && fileImportList.items && fileImportList.items.length]);

    const handleFetch = (type: string) => {
        const id = location.pathname.split('/').pop().split('&')[0];
        dispatch(
            workspaceActions?.getFileList(
                id,
                pageLimit,
                offset,
                sortField,
                sortDirection,
                type
            )
        );
    };

    const handleTableChange = (action: string, tableState: any) => {
        if (action !== 'propsUpdate') {
            const limit = tableState.rowsPerPage;
            const skip = tableState.rowsPerPage * tableState.page;
            const field = tableState.sortOrder.name || sortField;
            const direction =
                (tableState.sortOrder.direction &&
                    tableState.sortOrder.direction.toUpperCase()) ||
                (sortDirection && sortDirection.toUpperCase());
            const page = tableState.page;
            const id = location.pathname.split('/').pop().split('&')[0];

            dispatch(
                workspaceActions?.getFileList(
                    id,
                    limit,
                    skip,
                    field,
                    direction,
                    'initial'
                )
            );

            setOffset(skip);
            setPageLimit(limit);
            setSortField(field);
            setSortDirection(direction);
            setPageNumber(page);
        }
    };

    const handleFileDownload = (e, data) => {
        e.stopPropagation();
        const id = data[0];
        const fileName = data[1];
        dispatch(caseActions?.getFile(id, fileName));
    };

    const handleRowClick = (data) => {
        setFileName(data[1]);
        dispatch(workspaceActions.getImportStatus(data[0]));
        setOpenProgress(true);
    };

    const handleCloseProgress = () => {
        dispatch(workspaceActions?.resetFileImportStatus());
        setOpenErrorDialog(false);
        setOpenProgress(false);
    };

    const handleButtonClick = (e, data) => {
        e.stopPropagation();
        dispatch(workspaceActions.getImportStatus(data[0]));
        setOpenErrorDialog(true);
        setOpenProgress(false);
    };

    const handleCloseDialog = () => {
        setOpenProgress(false);
        setOpenErrorDialog(false);
    };

    const columns = [
        {
            name: 'id',
            label: 'ID',
            options: {
                display: false,
            },
        },
        {
            name: 'fileName',
            label: 'File Name',
            options: {
                customBodyRender: (value: string) => {
                    if (value) {
                        return value;
                    } else {
                        return '---';
                    }
                },
            },
        },
        {
            name: 'added',
            label: 'Date Added',
            options: {
                customBodyRender: (value: string) => {
                    if (value) {
                        return utils.formatDate(value, 'L LTS');
                    } else {
                        return '---';
                    }
                },
            },
        },
        {
            name: 'lastUpdated',
            label: 'Last Update',
            options: {
                customBodyRender: (value: string) => {
                    if (value) {
                        return utils.formatDate(value, 'L LTS');
                    } else {
                        return '---';
                    }
                },
            },
        },
        {
            name: 'status',
            label: 'Status',
            options: {
                customBodyRender: (value: string, tableMeta: any) => {
                    if (value === 'ERROR') {
                        return (
                            <Tooltip title={'An error occurred during import'}>
                                {statusTitle(value, 'table')}
                            </Tooltip>
                        );
                    } else if (value) {
                        return (
                            <Tooltip
                                title={utils.toTitleText(value.toLowerCase())}
                            >
                                {statusTitle(value, 'table')}
                            </Tooltip>
                        );
                    } else {
                        return '---';
                    }
                },
            },
        },
        {
            name: 'status',
            label: 'Clean Status',
            options: {
                display: false,
            },
        },
        {
            name: 'totalItems',
            label: 'Total Items',
            options: {
                customBodyRender: (value: string) => {
                    if (value) {
                        return value;
                    } else {
                        return '0';
                    }
                },
            },
        },
        {
            name: 'completedItems',
            label: 'Processed',
            options: {
                display: false,
                customBodyRender: (value: string) => {
                    if (value) {
                        return value;
                    } else {
                        return '0';
                    }
                },
            },
        },
        {
            name: 'erroredItems',
            label: 'Unable to Process',
            options: {
                display: false,
                customBodyRender: (value: string) => {
                    if (value) {
                        return value;
                    } else {
                        return '0';
                    }
                },
            },
        },
        {
            name: 'warningItems',
            label: 'Warning',
            options: {
                display: false,
                customBodyRender: (value: string) => {
                    if (value) {
                        return value;
                    } else {
                        return '0';
                    }
                },
            },
        },
        {
            name: 'hasError',
            label: 'Import Details',
            options: {
                setCellProps: () => ({
                    style: {
                        maxWidth: '250px',
                    },
                }),
                customBodyRender: (value: boolean, tableMeta: any) => {
                    const processedCount = tableMeta.rowData[7];
                    const errorCount = tableMeta.rowData[8];
                    const warningCount = tableMeta.rowData[9];
                    return (
                        <>
                            <Grid container alignItems="center">
                                {processedCount > 0 && (
                                    <Grid item xs={12}>
                                        <Grid
                                            container
                                            alignItems={'center'}
                                            spacing={1}
                                        >
                                            <Grid item>
                                                <CheckCircleOutlineIcon
                                                    color={'success'}
                                                    fontSize={'small'}
                                                />
                                            </Grid>
                                            <Grid item>
                                                <Typography variant={'caption'}>
                                                    Processed: {processedCount}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                )}
                                {errorCount > 0 && (
                                    <Grid item xs={12}>
                                        <Grid
                                            container
                                            alignItems={'center'}
                                            spacing={1}
                                        >
                                            <Grid item>
                                                <ErrorOutlineIcon
                                                    color={'error'}
                                                    fontSize={'small'}
                                                />
                                            </Grid>
                                            <Grid item>
                                                <Typography variant={'caption'}>
                                                    Unable to Process:{' '}
                                                    {errorCount}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                )}
                                {warningCount > 0 && (
                                    <Grid item xs={12}>
                                        <Grid
                                            container
                                            alignItems={'center'}
                                            spacing={1}
                                        >
                                            <Grid item>
                                                <WarningAmberIcon
                                                    color={'warning'}
                                                    fontSize={'small'}
                                                />
                                            </Grid>
                                            <Grid item>
                                                <Typography variant={'caption'}>
                                                    Warnings Found:{' '}
                                                    {warningCount}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                )}
                                {value && (
                                    <Grid item xs={12}>
                                        <Button
                                            style={{
                                                background: 'transparent',
                                            }}
                                            size="small"
                                            variant="text"
                                            onClick={(e) =>
                                                handleButtonClick(
                                                    e,
                                                    tableMeta.rowData
                                                )
                                            }
                                        >
                                            <Typography
                                                variant="caption"
                                                style={{
                                                    textDecoration: 'underline',
                                                    textTransform: 'none',
                                                }}
                                            >
                                                View Errors/Warnings Details
                                            </Typography>
                                        </Button>
                                    </Grid>
                                )}
                            </Grid>
                        </>
                    );
                },
            },
        },
        {
            name: 'trusted',
            label: 'Trusted',
            options: {
                customBodyRender: (value: string, tableMeta: any) => {
                    const fileId = tableMeta.rowData[0];
                    return (
                        <Trustness
                            type="file"
                            score={
                                trustData.files &&
                                trustData.files[fileId] &&
                                trustData.files[fileId].trust &&
                                trustData.files[fileId].trust.score
                            }
                            verified={
                                trustData.files &&
                                trustData.files[fileId] &&
                                trustData.files[fileId].trust &&
                                trustData.files[fileId].trust.verified
                            }
                            checking={
                                trustData.files &&
                                trustData.files[fileId] &&
                                trustData.files[fileId].checkingTrust
                            }
                            onExpertVerification={() => console.log('hi')}
                            disabled={false}
                        />
                    );
                },
            },
        },
        {
            name: 'download',
            label: 'Download',
            options: {
                customBodyRender: (value: string, tableMeta: any) => {
                    if (
                        tableMeta.rowData[4] === 'SUCCESS' ||
                        tableMeta.rowData[4] === 'ERROR'
                    ) {
                        return (
                            <IconButton
                                size={'small'}
                                onClick={(e) =>
                                    handleFileDownload(e, tableMeta.rowData)
                                }
                            >
                                <FileDownloadIcon />
                            </IconButton>
                        );
                    }
                },
            },
        },
    ];

    return (
        <Grid item xs={12} sx={{ width: '100%' }}>
            <Paper elevation={0} sx={{ p: '0px 16px 0px 16px' }}>
                <Table
                    loading={fileImportListLoading}
                    handleTableChange={handleTableChange}
                    count={fileImportList && fileImportList.total}
                    columns={columns}
                    pageLimit={pageLimit}
                    pageNumber={pageNumber}
                    sortOrder={{
                        direction: sortDirection.toLowerCase(),
                        name: sortField,
                    }}
                    data={
                        currentWorkspace &&
                        currentWorkspace.id &&
                        fileImportList &&
                        fileImportList.items &&
                        fileImportList.items.filter(
                            (item: { workspaceId: any }) =>
                                item.workspaceId === currentWorkspace.id
                        )
                    }
                    title={
                        <Grid container direction={'column'}>
                            <Grid item>
                                <Typography variant={'h6'}>
                                    Workspace File Uploads
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Typography variant={'body2'}>
                                    Click on a file to view its details and
                                    status report
                                </Typography>
                            </Grid>
                        </Grid>
                    }
                    onRowClick={handleRowClick}
                />
            </Paper>
            {!openErrorDialog ? (
                <Dialog
                    onClose={handleCloseProgress}
                    open={openProgress}
                    fullWidth
                    maxWidth={'lg'}
                    TransitionComponent={Transition}
                    keepMounted
                >
                    <ImportProgressPreview />
                    <DialogActions>
                        <Button onClick={handleCloseProgress}>Close</Button>
                    </DialogActions>
                </Dialog>
            ) : (
                <Dialog
                    open={openErrorDialog}
                    onClose={handleCloseDialog}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                    fullWidth
                    maxWidth={'lg'}
                    TransitionComponent={Transition}
                    keepMounted
                >
                    <ErrorsTable fileName={fileName} />
                    <DialogActions>
                        <Button onClick={handleCloseDialog} color="primary">
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </Grid>
    );
};

export default FileList;
